/**
 * 🎓 Günüm Var! - Uygulama Sabitleri
 * Bu dosya "Magic Numbers" ve "Magic Strings" kullanımını önler.
 *
 * ⚠️ KURAL: Kod içinde hiçbir hardcoded değer kullanma!
 * Her sabit buradan import edilmeli.
 */

// ============================================
// UYGULAMA GENEL
// ============================================

/** Uygulama adı */
export const APP_NAME = 'Günüm Var!'

/** Uygulama sloganı */
export const APP_SLOGAN = 'Sözünün Eri'

/** Varsayılan timezone */
export const DEFAULT_TIMEZONE = 'Europe/Istanbul'

/** Varsayılan dil */
export const DEFAULT_LANGUAGE = 'tr'

// ============================================
// KULLANICI LİMİTLERİ
// ============================================

/** Free kullanıcı maksimum aktif grup sayısı */
export const FREE_GROUP_LIMIT = 1

/** Pro kullanıcı maksimum aktif grup sayısı */
export const PRO_GROUP_LIMIT = 10

/** Minimum grup üye sayısı */
export const MIN_GROUP_MEMBERS = 3

/** Maksimum grup üye sayısı */
export const MAX_GROUP_MEMBERS = 24

/** Free kullanıcı maksimum davet süre uzatma hakkı */
export const FREE_MAX_INVITE_EXTENSIONS = 2

/** Pro kullanıcı maksimum davet süre uzatma hakkı */
export const PRO_MAX_INVITE_EXTENSIONS = 5

/** Davet süresi dolduktan sonra otomatik iptal süresi (saat) — Backend (execute-draw Stage F) ile senkron */
export const AUTO_CANCEL_HOURS = 24

/** Varsayılan grup üye sayısı */
export const DEFAULT_GROUP_MEMBERS = 12

// ============================================
// RÜTBE SİSTEMİ
// ============================================

/** Rütbe eşikleri (tamamlanan döngü sayısına göre) */
export const RANK_THRESHOLDS = {
  /** Çırak: 0-3 döngü */
  cirak: { min: 0, max: 3 },
  /** Kalfa: 4-10 döngü */
  kalfa: { min: 4, max: 10 },
  /** Usta: 11+ döngü */
  ustat: { min: 11, max: Infinity },
} as const

/** WCP (Ağırlıklı Döngü Puanı) rütbe eşikleri */
export const RANK_THRESHOLDS_WCP = {
  cirak: { min: 0 },
  kalfa: { min: 24 },
  ustat: { min: 72 },
} as const

/** WCP Periyot Çarpanları */
export const WCP_PERIOD_MULTIPLIER = {
  weekly: 1,
  monthly: 4,
} as const

/** Başlangıç itibar puanı */
export const INITIAL_REPUTATION_SCORE = 70

/** Maksimum itibar puanı */
export const MAX_REPUTATION_SCORE = 100

/** Minimum itibar puanı (bu değerin altına düşülemez) */
export const MIN_REPUTATION_SCORE = 0

/** Yıllık maksimum bonus puan limiti */
export const MAX_ANNUAL_BONUS_POINTS = 15

/** Yıllık maksimum comeback (geri dönüş) kullanım hakkı */
export const MAX_ANNUAL_COMEBACK = 2

/** Streak bonusu için ardışık ödeme sayısı eşiği */
export const STREAK_BONUS_INTERVAL = 3

/** Puan kademelerine göre motivasyon mesajları (Asla Puanım Düşük denmez) */
export const REPUTATION_MESSAGES = [
  { max: 29, text: 'Geri dön, seni bekliyoruz!', tone: 'invite' },
  { max: 49, text: 'Küçük adımlar, büyük güven.', tone: 'empathy' },
  { max: 69, text: 'Yeni başlangıçlar seni bekliyor!', tone: 'second_chance' },
  { max: 84, text: 'Harika gidiyorsun, devam et!', tone: 'encouragement' },
  { max: 100, text: 'Sözün altın kadar kıymetli!', tone: 'pride' },
] as const

// ============================================
// ÖDEME SİSTEMİ
// ============================================

/** Otomatik onay süresi (saat) — Backend (_shared/abacus.ts) ile senkron olmalı */
/**
 * Gec odeme sayilmakla çok gec odeme sayilmak arasindaki gun esigi.
 * Motorun kurali: calculatePaymentScore icinde `lateDays <= 3` -> LATE_PAYMENT.
 * Motorda adlandirilmis bir sabit yok; buradaki deger bir testle motorun
 * gercek davranisina karsi dogrulaniyor (reputation.ssot.test.ts).
 */
export const LATE_PAYMENT_GRACE_DAYS = 3

export const AUTO_CONFIRM_HOURS = 12

/** Ödeme hatırlatma saati (dakika - ödeme gününden önce) */
export const PAYMENT_REMINDER_MINUTES = 1440 // 24 saat

// ============================================
// ANİMASYON SÜRELERİ (ms)
// ============================================

export const ANIMATION_DURATION = {
  /** Hızlı animasyonlar (hover, fade) */
  fast: 150,
  /** Normal animasyonlar (modal, drawer) */
  normal: 300,
  /** Yavaş animasyonlar (page transition) */
  slow: 500,
  /** Kura çekimi animasyonu */
  lottery: 3000,
} as const

// ============================================
// UI SABİTLERİ
// ============================================

/** Input yüksekliği (px) - Erişilebilirlik için standarttan yüksek */
export const INPUT_HEIGHT = 56

/** Font boyutu - iOS zoom engellemek için minimum 16px */
export const MIN_FONT_SIZE = 17

/** Tab bar ikon boyutu */
export const TAB_ICON_SIZE = 28

/** Aksiyon ikon boyutu */
export const ACTION_ICON_SIZE = 24

/** Liste ikon boyutu */
export const LIST_ICON_SIZE = 20

// ============================================
// API & NETWORK
// ============================================

/** API istek zaman aşımı (ms) */
export const API_TIMEOUT = 10000

/** Yeniden deneme sayısı */
export const MAX_RETRIES = 3

/** Kur verisi güncelleme saati (UTC) */
export const RATE_UPDATE_HOUR_UTC = 7 // 10:00 Türkiye saati

// ============================================
// LOCAL STORAGE KEYS
// ============================================

export const STORAGE_KEYS = {
  theme: 'gunum-var-theme',
  language: 'gunum-var-language',
  lastSync: 'gunum-var-last-sync',
  fcmToken: 'gunum-var-fcm-token',
} as const

// ============================================
// ROUTE PATHS
// ============================================

export const ROUTES = {
  home: '/',
  login: '/login',
  loginVerify: '/login/verify',
  onboarding: '/onboarding',
  dashboard: '/dashboard',
  notifications: '/notifications',
  profile: '/profile',
  groups: {
    list: '/groups',
    new: '/groups/new',
    detail: (id: string) => `/groups/active?id=${id}`,
    invite: (id: string) => `/groups/active?id=${id}&tab=invite`,
    lottery: (id: string) => `/groups/active?id=${id}&tab=lottery`,
  },
  payment: {
    make: (groupId: string) => `/payment/${groupId}`,
    confirm: '/payment/confirm',
  },
  history: '/history',
} as const
